/**
 * Viewport membership index (EPIC-VIEWPORT-CLIP SLICE 3 —
 * `S-VIEWPORT-CLIP-RESOLVE`, membership half).
 *
 * A paper-space VIEWPORT windows model-space geometry: its paper-space frame
 * (center / width / height) maps to a model-space VIEW rectangle
 * (`viewCenter` / `viewHeight`, plus the paper aspect ratio and the view
 * twist). The membership index answers "which MODEL-SPACE entities does this
 * viewport actually show" — the set of model-space entity ids whose geometry
 * AABB intersects the viewport's model-space view rectangle.
 *
 * Why a SEPARATE module (not on the OBJECTS resolver): the clip BOUNDARY is an
 * OBJECTS-section / SPATIAL_FILTER concern (handleResolution.resolveViewportClip);
 * membership is a pure MODEL-SPACE spatial query over the typed drawing entities
 * and has no DXF-IO dependency. Keeping it a pure function (no React, no THREE,
 * no store) makes it unit-testable in isolation and lets the renderer's
 * stencil-clipped child pass consume `childEntityIds` without re-running the
 * spatial test per frame. Mirrors the bounds-helper shape of
 * `apps/web/src/app/scene/entityBounds.ts` but operates on the TYPED
 * `DrawingEntity` union rather than the `EntityPreview` string property bag.
 *
 * AutoCAD parity: AutoCAD culls a viewport's displayed model content by the
 * view extents (a per-VP visual rectangle). We use an axis-aligned bounding-box
 * intersection test — the canonical conservative window test (the same family
 * `entityBounds.ts` / `computeModelExtent` use). For a twisted viewport the
 * model view rectangle is rotated; we test against the AABB of its four rotated
 * corners, which is conservative (never drops a visible entity) — exactly how a
 * bbox cull behaves.
 */

import type {
  ArcEntity,
  BlockChildEntity,
  BlockInsertEntity,
  CircleEntity,
  DrawingEntity,
  EllipseEntity,
  HatchEntity,
  LineEntity,
  LwpolylineEntity,
  MtextEntity,
  OleFrameEntity,
  PointEntity,
  PolylineEntity,
  SplineEntity,
  TableEntity,
  TextEntity,
  ViewportEntity,
} from "./model";
import { bulgeSegmentBounds } from "./bulgeArc";
import { viewportWcsViewCenter } from "./viewportViewFrame";
import { tableAsBlockReference } from "./tableBlockReference";
import {
  heavyPolylineLocal,
  oleFrameLocal,
  type LocalPointSet,
} from "./wcsBoundsLeaf";
import { aabb3FromLocalPoints } from "./wcsBoundsMath";

/** Axis-aligned 2D bounding box (model space). */
export type EntityViewBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/**
 * The viewport's model-space VIEW rectangle as an axis-aligned bounding box.
 * For a non-twisted viewport this is the exact view window; for a twisted
 * viewport it is the AABB of the rotated view rectangle (conservative).
 */
export type ViewportViewRect = EntityViewBounds;

/**
 * Build an AABB from a non-empty list of 2D points. Empty ⇒ null.
 *
 * Exported (not module-private) so `@dxf-ai-editor/cad-core` — the OTHER
 * typed-geometry consumer of `drawing-model` — reuses this exact
 * accumulation instead of re-deriving it (one-truth AABB math; jscpd flagged
 * the near-identical duplicate before this export existed).
 */
export function boundsFromPoints(
  points: ReadonlyArray<{ x: number; y: number }>,
): EntityViewBounds | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  for (const p of points) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    any = true;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return any ? { minX, minY, maxX, maxY } : null;
}

/**
 * The model-space VIEW rectangle (AABB) shown by a viewport.
 *
 * Paper→model VIEW transform: the viewport's paper frame is `width × height`
 * centred at `center`; it maps to a model window of height `viewHeight` centred
 * at the WCS view centre, with model width `viewHeight × (width / height)` (the
 * paper aspect ratio preserved), rotated by `viewTwistAngle` (degrees, DXF group
 * 51 — the view twist rotates the model content inside the frame). We construct
 * the four rotated model-rect corners and return their AABB.
 *
 * The centre is {@link viewportWcsViewCenter}, NOT the raw `viewCenter`: groups
 * 12/22 are the centre in DCS, whose origin is the WCS view TARGET (17/27/37)
 * and whose axes are turned by the view twist, so the window is anchored at
 * `target + R(twist)·viewCenter` — the same rotation this function then applies
 * to the corner offsets AROUND that centre, which is the one rotation factored
 * over the sum (see that helper's `ezdxf`-derived twist section). The
 * same reading
 * anchors the renderer's per-VP camera and its cull rect; a membership index
 * anchored a whole target-vector away would hand per-viewport layer overrides
 * and annotation scales to the wrong entities. `viewTarget` is OPTIONAL in the
 * parameter type so `Pick`-shaped literals that predate it still compile — an
 * absent target is the WCS origin, i.e. the pre-target behaviour exactly.
 *
 * Returns null for a degenerate viewport (non-positive width/height/viewHeight),
 * matching the renderer's 0-area skip.
 */
export function computeViewportViewRect(
  viewport: Pick<
    ViewportEntity,
    "width" | "height" | "viewCenter" | "viewHeight" | "viewTwistAngle"
  > & {
    readonly viewTarget?: { readonly x: number; readonly y: number } | undefined;
  },
): ViewportViewRect | null {
  const { width, height, viewHeight } = viewport;
  if (!(width > 0) || !(height > 0) || !(viewHeight > 0)) return null;
  const viewCenter = viewportWcsViewCenter(viewport);

  const viewWidth = viewHeight * (width / height);
  const halfW = viewWidth / 2;
  const halfH = viewHeight / 2;

  // viewTwistAngle is in DEGREES (DXF group 51, same convention the renderer's
  // viewportTransform2d.paperToModel uses). Rotate the local rectangle corners
  // about the view centre and take their AABB.
  const theta = ((viewport.viewTwistAngle ?? 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const corner = (lx: number, ly: number): { x: number; y: number } => ({
    x: viewCenter.x + lx * cos - ly * sin,
    y: viewCenter.y + lx * sin + ly * cos,
  });
  const corners = [
    corner(-halfW, -halfH),
    corner(halfW, -halfH),
    corner(halfW, halfH),
    corner(-halfW, halfH),
  ];
  return boundsFromPoints(corners);
}

// ── Per-entity model-space bounds (mirrors scene/entityBounds.ts on the typed
//    DrawingEntity union) ────────────────────────────────────────────────────

function polarPoint(
  cx: number,
  cy: number,
  r: number,
  angleDeg: number,
): { x: number; y: number } {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function lineBounds(e: LineEntity): EntityViewBounds | null {
  return boundsFromPoints([e.start, e.end]);
}

function pointBounds(e: PointEntity): EntityViewBounds | null {
  return boundsFromPoints([e.location]);
}

function circleBounds(e: CircleEntity): EntityViewBounds | null {
  return {
    minX: e.center.x - e.radius,
    minY: e.center.y - e.radius,
    maxX: e.center.x + e.radius,
    maxY: e.center.y + e.radius,
  };
}

function arcBounds(e: ArcEntity): EntityViewBounds | null {
  // Endpoints + every cardinal angle (0/90/180/270) that lies on the swept arc
  // — mirrors entityBounds.ts. Angles are degrees, CCW.
  const pts = [
    polarPoint(e.center.x, e.center.y, e.radius, e.startAngle),
    polarPoint(e.center.x, e.center.y, e.radius, e.endAngle),
  ];
  const span = (((e.endAngle - e.startAngle) % 360) + 360) % 360;
  for (const cardinal of [0, 90, 180, 270]) {
    const rel = (((cardinal - e.startAngle) % 360) + 360) % 360;
    if (rel <= span) pts.push(polarPoint(e.center.x, e.center.y, e.radius, cardinal));
  }
  return boundsFromPoints(pts);
}

function ellipseBounds(e: EllipseEntity): EntityViewBounds | null {
  // Conservative AABB: the ellipse is bounded by the box ±(|major| , |minor|)
  // about the centre regardless of rotation (a rotated ellipse never exceeds
  // the circle of its major-axis radius), so pad the centre by the major-axis
  // length on each side. Exact swept-arc bounds would need the parametric
  // extrema; the conservative box never drops a visible ellipse (window-test
  // family). majorAxisEndpoint is relative to the centre.
  const major = Math.hypot(e.majorAxisEndpoint.x, e.majorAxisEndpoint.y);
  return {
    minX: e.center.x - major,
    minY: e.center.y - major,
    maxX: e.center.x + major,
    maxY: e.center.y + major,
  };
}

function splineBounds(e: SplineEntity): EntityViewBounds | null {
  // The curve is contained in the convex hull of its control points; the fit
  // points lie ON the curve. Either set bounds the spline conservatively.
  const pts = e.controlPoints.length > 0 ? e.controlPoints : e.fitPoints;
  return boundsFromPoints(pts);
}

function lwpolylineBounds(e: LwpolylineEntity): EntityViewBounds | null {
  // Per-SEGMENT AABB union including bulge arc extents. A bulged segment's arc
  // bows OUTSIDE the vertex hull, so a vertex-only AABB UNDER-reports the true
  // extent: a segment whose two endpoints sit outside the view rect but whose
  // arc bulges INTO it would be dropped (the membership window test must never
  // drop a visible entity — CLAUDE.md ПРИОРИТЕТ #1). `bulgeSegmentBounds`
  // (one-truth `bulgeArc`) adds the arc endpoints + crossed cardinal extrema;
  // for a straight segment it is the endpoint box. AutoCAD stores the bulge on
  // the STARTING vertex of each segment; a closed polyline adds the implicit
  // closing segment (last vertex → first vertex, with the last vertex's bulge).
  const vs = e.vertices;
  if (vs.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  const accumulate = (
    b: { minX: number; minY: number; maxX: number; maxY: number },
  ): void => {
    if (!Number.isFinite(b.minX) || !Number.isFinite(b.minY)) return;
    any = true;
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  };
  for (let i = 1; i < vs.length; i++) {
    accumulate(bulgeSegmentBounds(vs[i - 1]!, vs[i]!, vs[i - 1]!.bulge));
  }
  // The implicit closing segment (last→first) exists whenever the polyline is
  // CLOSED and has at least TWO vertices. A 2-vertex closed polyline is valid
  // and commonly a circle authored as two bulged semicircles: vertices i=1 walks
  // only the FIRST segment, so without this the closing arc (the second
  // semicircle) is dropped from the AABB and a viewport whose view rect the
  // closing arc bulges INTO would wrongly drop the polyline (Codex #326 P2).
  // Guard `> 2` was wrong — it skipped the closing segment for exactly 2 verts.
  if (e.closed && vs.length >= 2) {
    accumulate(
      bulgeSegmentBounds(vs[vs.length - 1]!, vs[0]!, vs[vs.length - 1]!.bulge),
    );
  }
  // A single-vertex / degenerate polyline never reached a segment: fall back to
  // the vertex box so an isolated point still contributes.
  if (!any) return boundsFromPoints(vs);
  return { minX, minY, maxX, maxY };
}

function textBounds(e: TextEntity): EntityViewBounds | null {
  return boundsFromPoints(
    e.alignmentPoint ? [e.insertionPoint, e.alignmentPoint] : [e.insertionPoint],
  );
}

function mtextBounds(e: MtextEntity): EntityViewBounds | null {
  return boundsFromPoints([e.insertionPoint]);
}

/**
 * The membership (XY) box of a kind whose extent the WCS bounds family already
 * owns — @codex PR #785 round 1, F2.
 *
 * `wcsBoundsLeaf.ts` is THE per-kind local-point truth (bulge-arc extrema,
 * elevation planes, thickness extrusion, poly-face FACE-record filtering) and
 * `wcsBoundsMath.ts::aabb3FromLocalPoints` is THE OCS→WCS box accumulation, so
 * a kind added here resolves through them instead of gaining a second, weaker
 * bounds implementation that could disagree with what the renderer draws. The
 * Z half is dropped: this index is a planar window test (see the module doc).
 *
 * Import direction: `wcsBoundsBlock.ts` imports FROM this module
 * (`ALWAYS_INCLUDE` / `resolveDefinitionWithCycleGuard`), so the block-backed
 * half of the WCS family must NOT be imported back — hence `Table` below goes
 * through this module's own `blockInsertBounds` on the shared
 * `tableAsBlockReference` view rather than through `wcsBoundsBlock`'s
 * `tableWcsBounds`. `wcsBoundsLeaf` / `wcsBoundsMath` import nothing from here,
 * so those two edges are cycle-free (dependency-cruiser `deps:graph:check`).
 */
function boundsFromWcsLocalPoints(set: LocalPointSet): EntityViewBounds | null {
  const box = aabb3FromLocalPoints(set.points, set.extrusion);
  return box
    ? { minX: box.minX, minY: box.minY, maxX: box.maxX, maxY: box.maxY }
    : null;
}

/**
 * A heavy `POLYLINE`'s membership box — all four subtypes through the one
 * truth: the 2D case walks its bulge arcs at the entity elevation (so an arc
 * that bows INTO the view rect is never dropped, exactly like LWPOLYLINE
 * above), while a 3D polyline / polygon mesh / poly-face mesh contributes its
 * real per-vertex points (FACE records excluded — they carry filler
 * coordinates, not geometry).
 */
function heavyPolylineBounds(e: PolylineEntity): EntityViewBounds | null {
  return boundsFromWcsLocalPoints(heavyPolylineLocal(e));
}

/**
 * An `OLE2FRAME`'s membership box: the FOUR corners of the frame rectangle
 * (the two stored corners alone are a diagonal, not a box). AutoCAD clips the
 * OLE picture to exactly this frame, so the frame IS the extent whether or not
 * the embedded raster is drawn.
 */
function oleFrameBounds(e: OleFrameEntity): EntityViewBounds | null {
  return boundsFromWcsLocalPoints(oleFrameLocal(e));
}

/**
 * An `ACAD_TABLE`'s membership box: the footprint of the anonymous `*T<N>`
 * block that carries its rendered graphics, placed by the table's insertion
 * point and its horizontal-direction vector.
 *
 * An ACAD_TABLE literally IS a block reference in DXF (first subclass
 * `AcDbBlockReference`, group 2 = the `*T<N>` block, 10/20/30 = the insertion
 * point), so it resolves through the SAME `blockInsertBounds` path a real
 * INSERT takes — including the never-drop `ALWAYS_INCLUDE` fallback for an
 * unresolvable definition — on the shared `tableAsBlockReference` projection
 * the WCS bounds walk and the spatial/metadata index already use. Anchoring on
 * the insertion point alone would drop a table whose grid spans into the view.
 */
function tableBounds(
  e: TableEntity,
  blockDefBounds?: Map<string, EntityViewBounds | typeof ALWAYS_INCLUDE>,
  blockBasePoints?: ReadonlyMap<string, { x: number; y: number }>,
): ResolvedEntityBounds {
  return blockInsertBounds(
    tableAsBlockReference(e),
    blockDefBounds,
    blockBasePoints,
  );
}

/**
 * Sentinel returned by the bounds helpers when an entity MUST be treated as a
 * viewport member without a finite AABB to test — i.e. the conservative
 * never-drop fallback (CLAUDE.md ПРИОРИТЕТ #1). Today only block INSERTs whose
 * definition bounds cannot be resolved (missing / cyclic / no usable geometry)
 * yield this: dropping such a block could hide content the viewport really
 * shows, whereas over-inclusion is harmless (the clipPath stencil culls
 * visually).
 */
export const ALWAYS_INCLUDE = "always-include" as const;
export type ResolvedEntityBounds =
  | EntityViewBounds
  | typeof ALWAYS_INCLUDE
  | null;

/**
 * Structural view of a block definition for bounds resolution. `basePoint` is
 * the DXF BLOCK base point (group 10/20) — subtracted before the insert scale /
 * rotate so a block authored around a non-zero base point computes its REAL
 * footprint (matches the renderer; Codex #326 P2). Optional so callers that pass
 * a base-point-at-origin block (the common case) need not supply it.
 */
type BlockDefForBounds = {
  entities: ReadonlyArray<BlockChildEntity>;
  basePoint?: { x: number; y: number };
};

/**
 * Apply an INSERT transform (insertionPoint + scaleX/Y + rotation, degrees CCW)
 * to a block-definition AABB and re-AABB the four rotated corners. Conservative
 * for a rotated insert exactly as `computeViewportViewRect` is for a twisted
 * view. Extrusion / OCS and the rarely-used non-uniform-shear cases are not
 * modelled (over-inclusion only — never an under-drop).
 *
 * `defBasePoint` is the block DEFINITION's base point (DXF BLOCK group 10/20).
 * AutoCAD places the block's base point onto the insertion point, so the FULL
 * insert transform is `T(insertion) × R × S × T(-basePoint)` — the def geometry
 * is first shifted so its base point lands at the local origin, THEN scaled /
 * rotated / translated. The renderer composes exactly this matrix
 * (`scene/blockMatrix.ts::composeBlockMatrix`). Omitting the `T(-basePoint)`
 * step shifts the computed footprint by `R·S·basePoint`, which can DROP a
 * visible insert from a viewport whose view actually contains it (Codex #326
 * P2). Default (0,0) ⇒ legacy behaviour for the common base-point-at-origin
 * block.
 *
 * Exported (not module-private) so `@dxf-ai-editor/cad-core`'s
 * `blockGeometry.ts` — which resolves a block INSERT's full per-kind /
 * MINSERT-aware footprint using cad-core's OWN typed per-kind bounds rather
 * than this module's narrower membership-only `computeBlockDefinitionBounds`
 * — reuses this exact insert-transform matrix instead of re-deriving it
 * (one-truth transform chain; mirrors the `boundsFromPoints` export above).
 */
export function transformBoundsByInsert(
  def: EntityViewBounds,
  e: BlockInsertEntity,
  defBasePoint?: { x: number; y: number },
): EntityViewBounds | null {
  const sx = e.scaleX;
  const sy = e.scaleY;
  const theta = ((e.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const ix = e.insertionPoint.x;
  const iy = e.insertionPoint.y;
  const bx = defBasePoint?.x ?? 0;
  const by = defBasePoint?.y ?? 0;
  const corner = (cx: number, cy: number): { x: number; y: number } => {
    // Block-space corner → minus basePoint → scale → rotate → translate to the
    // insertion point (matches composeBlockMatrix's right-to-left compose).
    const lx = (cx - bx) * sx;
    const ly = (cy - by) * sy;
    return { x: ix + lx * cos - ly * sin, y: iy + lx * sin + ly * cos };
  };
  return boundsFromPoints([
    corner(def.minX, def.minY),
    corner(def.maxX, def.minY),
    corner(def.maxX, def.maxY),
    corner(def.minX, def.maxY),
  ]);
}

/**
 * Real model-space AABB of a block INSERT: the block DEFINITION's child-geometry
 * AABB transformed by the INSERT (insertionPoint + scale + rotation). A block
 * whose insertion point lies OUTSIDE the view but whose geometry spans INTO it
 * must still be a member, so anchoring on the insertion point alone (the old
 * behaviour) wrongly DROPS it (CLAUDE.md ПРИОРИТЕТ #1, full fidelity).
 *
 * Conservative never-drop fallback: when the definition bounds cannot be
 * resolved (missing / cyclic / no usable geometry — value ALWAYS_INCLUDE, or no
 * `blockDefBounds` context at all) we return ALWAYS_INCLUDE so the INSERT is
 * never dropped, rather than falling back to the misleading insertion point.
 */
function blockInsertBounds(
  e: BlockInsertEntity,
  blockDefBounds?: Map<string, EntityViewBounds | typeof ALWAYS_INCLUDE>,
  blockBasePoints?: ReadonlyMap<string, { x: number; y: number }>,
): ResolvedEntityBounds {
  if (!blockDefBounds) return ALWAYS_INCLUDE;
  const def = blockDefBounds.get(e.blockName);
  if (def === undefined || def === ALWAYS_INCLUDE) return ALWAYS_INCLUDE;
  // Subtract the DEFINITION's base point before the insert transform (matches
  // the renderer's composeBlockMatrix `T(-basePoint)` step; Codex #326 P2).
  const transformed = transformBoundsByInsert(
    def,
    e,
    blockBasePoints?.get(e.blockName),
  );
  // A degenerate transform (non-finite) ⇒ never drop.
  return transformed ?? ALWAYS_INCLUDE;
}

function hatchBounds(e: HatchEntity): EntityViewBounds | null {
  // Union the AABB over every boundary-path edge of the typed edge union
  // (`hatchBoundaryEdgeSchema`): line ⇒ start/end; arc ⇒ center ± radius box;
  // ellipticArc ⇒ center ± |majorAxisEnd| box (conservative, rotation-safe).
  const pts: Array<{ x: number; y: number }> = [];
  for (const path of e.boundaryPaths) {
    for (const edge of path.edges) {
      switch (edge.edgeType) {
        case "line":
          pts.push(edge.start, edge.end);
          break;
        case "arc":
          pts.push(
            { x: edge.center.x - edge.radius, y: edge.center.y - edge.radius },
            { x: edge.center.x + edge.radius, y: edge.center.y + edge.radius },
          );
          break;
        case "ellipticArc": {
          const major = Math.hypot(edge.majorAxisEnd.x, edge.majorAxisEnd.y);
          pts.push(
            { x: edge.center.x - major, y: edge.center.y - major },
            { x: edge.center.x + major, y: edge.center.y + major },
          );
          break;
        }
      }
    }
  }
  return boundsFromPoints(pts);
}

/**
 * Axis-aligned model-space bounds of a typed drawing entity, or null when the
 * entity carries no usable positional geometry (or is a type whose bounds we do
 * not compute — those simply never appear in the membership set). Mirrors
 * `apps/web/src/app/scene/entityBounds.ts` on the typed `DrawingEntity` union.
 *
 * `blockDefBounds` (optional) maps block name → its model-space definition AABB
 * (or ALWAYS_INCLUDE for an unresolvable def). When provided, a block INSERT's
 * real footprint is its DEFINITION AABB transformed by the insert; when absent,
 * a block INSERT conservatively resolves to ALWAYS_INCLUDE (never dropped).
 * `blockBasePoints` (optional) maps block name → its DXF base point so the
 * insert transform subtracts it (matches the renderer; Codex #326 P2). Non-block
 * entities ignore both parameters. The return is ALWAYS_INCLUDE for the
 * conservative never-drop fallback (block INSERTs only — see `blockInsertBounds`).
 */
export function entityModelBounds(
  entity: DrawingEntity,
  blockDefBounds?: Map<string, EntityViewBounds | typeof ALWAYS_INCLUDE>,
  blockBasePoints?: ReadonlyMap<string, { x: number; y: number }>,
): ResolvedEntityBounds {
  switch (entity.type) {
    case "Line":
      return lineBounds(entity);
    case "Point":
      return pointBounds(entity);
    case "Circle":
      return circleBounds(entity);
    case "Arc":
      return arcBounds(entity);
    case "Ellipse":
      return ellipseBounds(entity);
    case "Spline":
      return splineBounds(entity);
    case "LWPOLYLINE":
      return lwpolylineBounds(entity);
    case "Text":
      return textBounds(entity);
    case "MText":
      return mtextBounds(entity);
    case "Block":
      return blockInsertBounds(entity, blockDefBounds, blockBasePoints);
    case "Hatch":
      return hatchBounds(entity);
    // @codex PR #785 round 1, F2 — the three kinds `drawingEntitySchema` newly
    // admits. Without these arms their ids fell to `default: null` and were
    // omitted from EVERY viewport's `childEntityIds`, so geometry visible in
    // model space vanished when viewed through a layout viewport.
    case "Polyline":
      return heavyPolylineBounds(entity);
    case "OLE Frame":
      return oleFrameBounds(entity);
    case "Table":
      return tableBounds(entity, blockDefBounds, blockBasePoints);
    default:
      // Dimension / Multileader / Revision Cloud / Wipeout / Array / Viewport:
      // not part of the membership query (a Viewport never lives in model space;
      // the rest are either paper-space annotations or have no simple model AABB
      // contribution for the clip-child pass). Absent ⇒ never a member.
      return null;
  }
}

/** Resolved block-definition bounds + the base-point lookup used to transform
 * inserts of those blocks. The two maps are produced together by
 * `computeBlockDefinitionBounds` and consumed together by
 * `buildViewportMembershipIndex` / `entityModelBounds`. */
export type BlockDefinitionBounds = {
  /** block name → model-space definition AABB (or ALWAYS_INCLUDE). */
  bounds: Map<string, EntityViewBounds | typeof ALWAYS_INCLUDE>;
  /** block name → DXF base point (group 10/20) — only for blocks with a
   * non-undefined base point; absent ⇒ treated as (0,0). */
  basePoints: Map<string, { x: number; y: number }>;
};

/**
 * Generic memoized, cycle-safe "resolve a named block definition" wrapper —
 * shared by this module's `computeBlockDefinitionBounds` AND cad-core's
 * `blockGeometry.ts::resolveDefinitionBounds` (a DIFFERENT per-kind
 * accumulation over the SAME block-definition graph shape). Both need the
 * identical skeleton: already resolved ⇒ return the cached value; missing
 * definition OR mid-resolution (a cycle) ⇒ conservatively `ALWAYS_INCLUDE`
 * (never drop); otherwise mark `name` in-progress, run the caller's own
 * per-kind child accumulation (via `accumulate`, which may recurse into this
 * same wrapper for a nested Block child), then memoize. Exported (not
 * module-private) for that cross-package reuse — one truth for the
 * memoize+cycle-guard shape (jscpd flagged the near-identical duplicate
 * before this export existed; mirrors the `boundsFromPoints` /
 * `transformBoundsByInsert` exports above).
 */
export function resolveDefinitionWithCycleGuard<T>(
  name: string,
  hasDefinition: (name: string) => boolean,
  resolved: Map<string, T | typeof ALWAYS_INCLUDE>,
  inProgress: Set<string>,
  accumulate: () => T | typeof ALWAYS_INCLUDE,
): T | typeof ALWAYS_INCLUDE {
  const cached = resolved.get(name);
  if (cached !== undefined) return cached;
  if (!hasDefinition(name)) return ALWAYS_INCLUDE; // missing definition ⇒ never drop
  if (inProgress.has(name)) return ALWAYS_INCLUDE; // cycle ⇒ never drop
  inProgress.add(name);
  const result = accumulate();
  inProgress.delete(name);
  resolved.set(name, result);
  return result;
}

/**
 * Own-property-safe lookup into a plain `Record<string, T>` — mirrors
 * `wcsBoundsBlock.ts::safeRecordLookup` EXACTLY (@codex T1 arm(c) round-24 P2 finding,
 * additional-consistency fix for this module's own pre-existing identical
 * `blocks[n] !== undefined` / `blocks[name]!` pattern below): a bare `record[key]` index
 * silently falls through to an INHERITED `Object.prototype` member (a function, not a `T`) for a
 * key like `"constructor"`/`"toString"`/`"__proto__"` instead of returning `undefined`. Not
 * imported from `wcsBoundsBlock.ts` — that module already imports FROM this one
 * (`resolveDefinitionWithCycleGuard`/`ALWAYS_INCLUDE`), so importing back would be a circular
 * dependency; re-derived here instead, same precedent as `cad-core/common.ts`'s
 * `isArrayAxisAngleRadians` being re-derived in `wcsBoundsLeaf.ts` for its own cross-boundary
 * dependency-direction reason.
 */
function safeBlockLookup<T>(record: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

/**
 * Resolve every block definition's model-space child-geometry AABB into a map
 * `block name → AABB | ALWAYS_INCLUDE`, recursively transforming nested INSERT
 * children by their own insert transform (incl. each child block's base point).
 * Also returns the per-block base-point lookup so the membership pass subtracts
 * the def base point when transforming a TOP-LEVEL insert (matches the renderer;
 * Codex #326 P2). Both maps are consumed by `entityModelBounds` /
 * `buildViewportMembershipIndex` so a block INSERT tests its REAL footprint, not
 * just its insertion point.
 *
 * Resolution rules (conservative — never under-report):
 *   - A child whose own bounds are ALWAYS_INCLUDE (a nested INSERT to an
 *     unresolvable / cyclic def) makes the WHOLE containing definition
 *     ALWAYS_INCLUDE — we cannot bound it, so any insert of it must never drop.
 *   - A cycle (block A → B → A) marks every block on the cycle ALWAYS_INCLUDE.
 *   - A definition with no usable geometry resolves to ALWAYS_INCLUDE as well
 *     (an empty/attribute-only block could still carry an ATTRIB the user sees;
 *     never drop) rather than null.
 */
export function computeBlockDefinitionBounds(
  blocks: Readonly<Record<string, BlockDefForBounds>>,
): BlockDefinitionBounds {
  const resolved = new Map<string, EntityViewBounds | typeof ALWAYS_INCLUDE>();
  const basePoints = new Map<string, { x: number; y: number }>();
  const inProgress = new Set<string>();

  // Seed the base-point lookup up-front so nested-insert transforms (below) can
  // read a child block's base point regardless of resolution order.
  for (const [name, def] of Object.entries(blocks)) {
    if (def.basePoint) {
      basePoints.set(name, { x: def.basePoint.x, y: def.basePoint.y });
    }
  }

  const resolve = (name: string): EntityViewBounds | typeof ALWAYS_INCLUDE =>
    resolveDefinitionWithCycleGuard(name, (n) => safeBlockLookup(blocks, n) !== undefined, resolved, inProgress, () => {
      const def = safeBlockLookup(blocks, name)!;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      let any = false;
      let mustInclude = false;

      for (const child of def.entities) {
        let childBounds: EntityViewBounds | typeof ALWAYS_INCLUDE | null;
        if (child.type === "Block") {
          const childDef = resolve(child.blockName);
          childBounds =
            childDef === ALWAYS_INCLUDE
              ? ALWAYS_INCLUDE
              : // Subtract the NESTED def's base point too (the inner insert places
                // that block's base point onto its insertion point).
                transformBoundsByInsert(
                  childDef,
                  child,
                  basePoints.get(child.blockName),
                );
        } else {
          // BlockChildEntity (Line/Arc/.../Hatch/Dimension/Multileader/Attdef)
          // is a subset of DrawingEntity; entityModelBounds handles the geometry
          // members and returns null for the rest (Attdef/Dimension/Multileader).
          childBounds = entityModelBounds(
            child as DrawingEntity,
            resolved,
            basePoints,
          );
        }
        if (childBounds === ALWAYS_INCLUDE) {
          mustInclude = true;
          break;
        }
        if (childBounds) {
          any = true;
          if (childBounds.minX < minX) minX = childBounds.minX;
          if (childBounds.minY < minY) minY = childBounds.minY;
          if (childBounds.maxX > maxX) maxX = childBounds.maxX;
          if (childBounds.maxY > maxY) maxY = childBounds.maxY;
        }
      }

      return mustInclude || !any ? ALWAYS_INCLUDE : { minX, minY, maxX, maxY };
    });

  for (const name of Object.keys(blocks)) resolve(name);
  return { bounds: resolved, basePoints };
}

/** Whether two AABBs intersect (touching edges count as intersecting). */
function boundsIntersect(a: EntityViewBounds, b: EntityViewBounds): boolean {
  return !(
    a.maxX < b.minX ||
    a.minX > b.maxX ||
    a.maxY < b.minY ||
    a.minY > b.maxY
  );
}

/**
 * Build the per-viewport membership index: for each VIEWPORT, the model-space
 * entity ids whose geometry AABB intersects the viewport's model-space view
 * rectangle.
 *
 * Pure function — no side effects. O(V × E) over viewports × model entities;
 * each entity's bounds is computed once and reused across viewports.
 *
 * @param modelSpaceEntities the MODEL-space entities (NOT paper-space), as any
 *        single-pass `Iterable` (an array OR a lazy generator). The function
 *        walks it EXACTLY ONCE, computing a slim `{id, AABB}` per entity and
 *        retaining ONLY that — so a streaming caller can pass a compact-cache
 *        generator (`iterateSpaceEntity`) and never materialise the full
 *        `DrawingEntity[]` (Codex #326 r3 P2 #4: avoid the O(entities) snapshot
 *        that re-introduced the ~150 MB peak the streaming-import path avoids).
 *        A Viewport entity that somehow appears here is ignored (no model AABB).
 * @param viewports the paper-space VIEWPORT entities to index.
 * @param blockDefBounds optional resolved block bounds + base-point lookup —
 *        produce it once via `computeBlockDefinitionBounds(doc.blocks)`. When
 *        supplied, a block INSERT tests its real DEFINITION footprint
 *        (transformed by the insert, base point subtracted) instead of its
 *        insertion point; an unresolvable def conservatively makes the INSERT a
 *        member of every non-degenerate viewport (never dropped). When omitted,
 *        every block INSERT is conservatively included.
 * @returns Map of viewport id → list of member entity ids (insertion order of
 *          `modelSpaceEntities`). A viewport with no view rect (degenerate) or
 *          no members maps to an empty array — present so consumers can
 *          distinguish "computed, none" from "not computed".
 */
export function buildViewportMembershipIndex(
  modelSpaceEntities: Iterable<DrawingEntity>,
  viewports: ReadonlyArray<ViewportEntity>,
  blockDefBounds?: BlockDefinitionBounds,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const bounds = blockDefBounds?.bounds;
  const basePoints = blockDefBounds?.basePoints;

  // Pre-compute each model entity's bounds ONCE, preserving insertion order in a
  // single list. `bounds === null` flags an ALWAYS_INCLUDE entity (an
  // unresolvable block INSERT) — it has no finite AABB to test, so it is a
  // member of EVERY non-degenerate viewport (conservative never-drop). Entities
  // with no membership contribution at all are simply not pushed.
  const entityBounds: Array<{ id: string; bounds: EntityViewBounds | null }> =
    [];
  for (const e of modelSpaceEntities) {
    if (e.type === "Viewport") continue;
    const b = entityModelBounds(e, bounds, basePoints);
    if (b === ALWAYS_INCLUDE) {
      entityBounds.push({ id: e.id, bounds: null });
    } else if (b) {
      entityBounds.push({ id: e.id, bounds: b });
    }
  }

  for (const vp of viewports) {
    const rect = computeViewportViewRect(vp);
    const members: string[] = [];
    if (rect) {
      for (const { id, bounds } of entityBounds) {
        // null bounds ⇒ ALWAYS_INCLUDE; otherwise window-test the AABB.
        if (bounds === null || boundsIntersect(bounds, rect)) members.push(id);
      }
    }
    out.set(vp.id, members);
  }

  return out;
}
